import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { useGame } from '../context/GameContext';
import { Button } from '../components/common/Button';
import { InlineCodeText } from '../components/common/InlineCodeText';
import { loadCompletedLevelSummaries, type CompletedLevelSummary } from '../services/progressService';

export function ProgressPage() {
  const { gameData } = useGame();
  const completedKeys = gameData?.progress.completedLevels;
  const [completedLevels, setCompletedLevels] = useState<CompletedLevelSummary[]>([]);
  const [loadingNames, setLoadingNames] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoadingNames(true);
    setLoadError(false);
    loadCompletedLevelSummaries(completedKeys ?? [])
      .then(levels => { if (!cancelled) setCompletedLevels(levels); })
      .catch(() => { if (!cancelled) setLoadError(true); })
      .finally(() => { if (!cancelled) setLoadingNames(false); });
    return () => { cancelled = true; };
  }, [completedKeys, retry]);

  if (!gameData) {
    return <div className="text-gray-900 dark:text-gray-100">Loading...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-6">Your Progress</h1>
      
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 dark:text-gray-100">Statistics</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Level</p>
            <p className="text-2xl font-bold dark:text-gray-100">{gameData.progress.level}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">XP</p>
            <p className="text-2xl font-bold dark:text-gray-100">{gameData.progress.xp}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Completed</p>
            <p className="text-2xl font-bold dark:text-gray-100">{gameData.progress.completedLevels.length}</p>
          </div>
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400">Tactics</p>
            <p className="text-2xl font-bold dark:text-gray-100">{gameData.unlockedTactics.length}</p>
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-xl font-semibold mb-4 dark:text-gray-100">Completed Levels</h2>
        {gameData.progress.completedLevels.length === 0 ? (
          <p className="text-gray-600 dark:text-gray-300">No levels completed yet. Choose a world to get started.</p>
        ) : loadingNames ? (
          <p role="status" className="text-gray-600 dark:text-gray-300">Loading completed exercises…</p>
        ) : loadError ? (
          <div className="space-y-3">
            <p role="alert" className="text-gray-600 dark:text-gray-300">Could not load exercise names. Your saved progress is still available.</p>
            <Button type="button" variant="secondary" size="sm" onClick={() => setRetry(previous => previous + 1)}>Try again</Button>
          </div>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {completedLevels.map(level => (
              <li key={level.key} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                <CheckCircle2 size={18} className="mt-1 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
                <div className="min-w-0">
                  {level.href ? (
                    <Link to={level.href} className="font-medium text-primary-700 hover:underline dark:text-primary-300"><InlineCodeText>{level.levelName ?? 'Completed exercise'}</InlineCodeText></Link>
                  ) : (
                    <span className="font-medium text-gray-700 dark:text-gray-200">Completed exercise</span>
                  )}
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{level.worldName ?? 'Exercise name unavailable in the current catalogue.'}</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-6">
        <Link to="/">
          <Button>Back to Home</Button>
        </Link>
      </div>
    </div>
  );
}
